import { StyleSheet } from "react-native-unistyles";

import isIPad from "$src/core/utils/device/is-ipad";
import CustomPlatform from "$src/core/utils/platform/platform";

// Android TV reports no overscan inset; 5% is what Apple and Google reserve.
const TV_OVERSCAN_RATIO = CustomPlatform.select({
  androidTV: 0.05,
  default: 0,
});

const overscan = (inset: number, axisLength: number) =>
  Math.max(inset, axisLength * TV_OVERSCAN_RATIO);

type VerticalOptions = {
  tabBarHeight?: number;
};

const $pageInsets = StyleSheet.create(
  ({ spacing, breakpoints }, { insets, screen }) => {
    const isTabBarAtTop = !!isIPad;

    const top = CustomPlatform.select({
      native: ({ tabBarHeight = 0 }: VerticalOptions = {}) => ({
        paddingTop: {
          xs: spacing(6) + insets.top + (isTabBarAtTop ? tabBarHeight : 0),
          md: spacing(8) + insets.top + (isTabBarAtTop ? tabBarHeight : 0),
        },
      }),
      tv: ({ tabBarHeight = 0 }: VerticalOptions = {}) => ({
        paddingTop: {
          xs: overscan(insets.top, screen.height) + tabBarHeight,
        },
      }),
      web: ({ tabBarHeight = 0 }: VerticalOptions = {}) => ({
        paddingTop: {
          xs: spacing(6) + insets.top,
          md: spacing(8) + insets.top,
          lg: spacing(8) + tabBarHeight + insets.top,
        },
      }),
      default: (_: VerticalOptions = {}) => ({}),
    });

    const bottom = CustomPlatform.select({
      native: ({ tabBarHeight = 0 }: VerticalOptions = {}) => ({
        paddingBottom: {
          xs: spacing(6) + insets.bottom + (isTabBarAtTop ? 0 : tabBarHeight),
          md: spacing(8) + insets.bottom + (isTabBarAtTop ? 0 : tabBarHeight),
        },
      }),
      tv: (_: VerticalOptions = {}) => ({
        paddingBottom: {
          xs: spacing(8) + overscan(insets.bottom, screen.height),
        },
      }),
      web: (_: VerticalOptions = {}) => ({
        paddingBottom: {
          xs: spacing(6) + insets.bottom,
          md: spacing(8) + insets.bottom,
        },
      }),
      default: (_: VerticalOptions = {}) => ({}),
    });

    const left = CustomPlatform.select({
      // For native/tv platforms:
      // Apply padding to the left and right of the section based on the screen width.
      // This way a max width of `breakpoints.[xxl|xxxl]` is applied to the section, without cutting off the content.
      // We're adding the insets here too, because for example in landscape mode a notch can be on the left or right side.
      native: {
        paddingLeft: {
          xs:
            Math.max(screen.width - breakpoints.xxl, 0) / 2 +
            spacing(4) +
            insets.left,
          md:
            Math.max(screen.width - breakpoints.xxl, 0) / 2 +
            spacing(5) +
            insets.left,
        },
      },
      tv: {
        paddingLeft: {
          xs:
            Math.max(screen.width - breakpoints.xxxl, 0) / 2 +
            spacing(5) +
            overscan(insets.left, screen.width),
        },
      },

      // For web platform:
      // Apply padding to the left and right of the section based on the screen width.
      // This way a max width of `breakpoints.xxl` is applied to the section, without cutting off the content.
      // Pure CSS is more performant in this case, so that's why we're using it for web.
      web: {
        _web: {
          paddingLeft: {
            xs: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(4)}px)`,
            md: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(6)}px)`,
            lg: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(14)}px)`,
          },
        },
      },
    });

    const right = CustomPlatform.select({
      // For native/tv platforms:
      // Apply padding to the left and right of the section based on the screen width.
      // This way a max width of `breakpoints.[xxl|xxxl]` is applied to the section, without cutting off the content.
      // We're adding the insets here too, because for example in landscape mode a notch can be on the left or right side.
      native: {
        paddingRight: {
          xs:
            Math.max(screen.width - breakpoints.xxl, 0) / 2 +
            spacing(4) +
            insets.right,
          md:
            Math.max(screen.width - breakpoints.xxl, 0) / 2 +
            spacing(5) +
            insets.right,
        },
      },
      default: {
        paddingRight: {
          xs:
            Math.max(screen.width - breakpoints.xxxl, 0) / 2 +
            spacing(5) +
            overscan(insets.right, screen.width),
        },
      },

      // For web platform:
      // Apply padding to the left and right of the section based on the screen width.
      // This way a max width of `breakpoints.xxl` is applied to the section, without cutting off the content.
      // Pure CSS is more performant in this case, so that's why we're using it for web.
      web: {
        _web: {
          paddingRight: {
            xs: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(4)}px)`,
            md: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(6)}px)`,
            lg: `calc(max(calc(calc(100% - ${breakpoints.xxl}px) / 2), 0px) + ${spacing(14)}px)`,
          },
        },
      },
    });

    return {
      top,
      bottom,
      vertical: (options: VerticalOptions = {}) => ({
        ...top(options),
        ...bottom(options),
      }),
      left,
      right,
      horizontal: {
        ...left,
        ...right,
        _web: {
          ...left._web,
          ...right._web,
        },
      },
    };
  },
);

export default $pageInsets;
